import { useI18n } from "@/shared/i18n"
import type { UserGift } from "@/shared/api/types"

import { GiftCard } from "./GiftCard"

interface GiftsGridProps {
  gifts: UserGift[]
  isLoading?: boolean
  isError?: boolean
  onSelect?: (gift: UserGift) => void
  onRetry?: () => void
}

export function GiftsGrid({
  gifts,
  isLoading,
  isError,
  onSelect,
  onRetry,
}: GiftsGridProps) {
  const { t } = useI18n()

  if (isLoading && gifts.length === 0) {
    return (
      <div className="px-4 py-6">
        <div className="grid grid-cols-3 justify-items-center gap-x-5 gap-y-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div
              key={idx}
              className="flex w-full flex-col items-center gap-1"
            >
              <div className="h-28 w-full animate-pulse rounded-2xl bg-secondary-main" />
              <div className="h-4 w-20 animate-pulse rounded bg-secondary-main" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
        <span className="text-sm text-white/60">
          {t("inventory.loadFailed")}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-white"
          >
            {t("inventory.retry")}
          </button>
        )}
      </div>
    )
  }

  if (gifts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
        <span className="text-base font-medium text-white">
          {t("inventory.emptyTitle")}
        </span>
        <span className="max-w-[280px] text-sm text-white/60">
          {t("inventory.emptyHint")}
        </span>
      </div>
    )
  }

  return (
    <div className="px-4 py-4">
      <div className="grid grid-cols-3 justify-items-center gap-x-5 gap-y-6">
        {gifts.map((gift) => (
          <GiftCard key={gift.id} gift={gift} onClick={onSelect} />
        ))}
      </div>
    </div>
  )
}
